import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, DOCUMENT, ErrorHandler, inject, PLATFORM_ID, Service } from '@angular/core';
import { AlertService } from '@app/core/messages/alert.service';
import { AuthClient } from '@shared/api/identity/clients/auth.client';
import { SessionStore } from './session-store';
import { decodeTokenPayload } from './token-payload.util';

const CHECK_INTERVAL_MS = 30_000;
const WARNING_BEFORE_MS = 2 * 60_000;
const ACTIVITY_EVENTS = ['click', 'keydown', 'scroll', 'pointerdown'];

class SessionRenewalSilentlyFailedError extends Error {
  constructor() {
    super('a sessão não foi renovada: o cabeçalho de token renovado não chegou na resposta. Confira se o servidor o envia e se ele está exposto na política de origem cruzada.');
    this.name = 'SessionRenewalSilentlyFailedError';
  }
}

@Service()
export class SessionRenewalService {
  private readonly sessionStore = inject(SessionStore);
  private readonly authClient = inject(AuthClient);
  private readonly alertService = inject(AlertService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly errorHandler = inject(ErrorHandler);

  private interacted = false;
  private warned = false;
  private renewing = false;
  private reportedSilentRenewal = false;

  init(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const remember = () => (this.interacted = true);

    for (const event of ACTIVITY_EVENTS) {
      this.document.addEventListener(event, remember, { passive: true });
    }

    const onVisible = () => this.check();
    this.document.addEventListener('visibilitychange', onVisible);

    const timer = setInterval(() => this.check(), CHECK_INTERVAL_MS);

    this.destroyRef.onDestroy(() => {
      clearInterval(timer);
      this.document.removeEventListener('visibilitychange', onVisible);

      for (const event of ACTIVITY_EVENTS) {
        this.document.removeEventListener(event, remember);
      }
    });
  }

  private check(): void {
    if (this.renewing) {
      return;
    }

    if (!this.sessionStore.isSignedIn()) {
      this.warned = false;

      return;
    }

    const lifetime = this.tokenLifetimeMs();
    const age = this.sessionStore.tokenAgeMs();

    if (lifetime === null || age === null) {
      return;
    }

    if (age >= lifetime / 2 && this.interacted) {
      this.renew();

      return;
    }

    if (lifetime - age <= WARNING_BEFORE_MS && !this.warned) {
      this.warned = true;
      this.alertService.warning('Sua sessão vai expirar em instantes. Use o sistema para continuar conectado.');
    }
  }

  private renew(): void {
    this.renewing = true;
    const ageBeforeRenewal = this.sessionStore.tokenAgeMs();

    this.authClient.keepSessionAlive().subscribe({
      next: () => {
        this.reportWhenNoTokenArrived(ageBeforeRenewal);
        this.interacted = false;
        this.warned = false;
        this.renewing = false;
      },
      error: () => (this.renewing = false),
    });
  }

  private reportWhenNoTokenArrived(ageBeforeRenewal: number | null): void {
    const ageAfterRenewal = this.sessionStore.tokenAgeMs();
    const tokenArrived = ageBeforeRenewal === null || ageAfterRenewal === null || ageAfterRenewal < ageBeforeRenewal;

    if (this.reportedSilentRenewal || tokenArrived) {
      return;
    }

    this.reportedSilentRenewal = true;
    this.errorHandler.handleError(new SessionRenewalSilentlyFailedError());
  }

  private tokenLifetimeMs(): number | null {
    const token = this.sessionStore.getToken();

    if (!token) {
      return null;
    }

    const payload = decodeTokenPayload(token);
    const issuedAt = payload?.['iat'];
    const expiresAt = payload?.['exp'];

    if (typeof issuedAt !== 'number' || typeof expiresAt !== 'number') {
      return null;
    }

    const lifetime = (expiresAt - issuedAt) * 1000;

    return lifetime > 0 ? lifetime : null;
  }
}
